import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, DatePicker, Select, Spin, Table, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService, { ServiceType } from '../../../services/api';
import { formatInt, formatDecimal } from '../../../utils/numberFormat';
import { downloadCsv, toCsv } from '../../../utils/csv';

const coreService = apiService.getService(ServiceType.CORE);

const { Text } = Typography;

// ----- API response types (mirror src/grid/dto/grid-profit-response.dto.ts) -----
interface SymbolProfit {
  symbol: string;
  instrumentId: string;
  grossPnl: number;
  creditPnl: number;
  fee: number;
  netPnl: number;
  positionCount: number;
}
interface ProfitSummary {
  grossPnl: number;
  /** Corporate-action cash, net of tax. See CreditRow. */
  creditPnl: number;
  totalFee: number;
  netPnl: number;
  bySymbol: SymbolProfit[];
  firstClosedAt: string | null;
  lastClosedAt: string | null;
  dataGapBefore: string | null;
  /** Applied cash actions whose money never reached these numbers. */
  uncountedCredits?: UncountedCredit[];
}
interface UncountedCredit {
  groupId: string;
  symbol: string;
  cashPerShare: number;
  exDate: string | null;
  appliedAt: string;
}
interface DailyPoint {
  date: string;
  grossPnl: number;
  creditPnl: number;
  fee: number;
  netPnl: number;
}
/**
 * Cash a corporate action credited.
 *
 * Its own row on purpose: a cash dividend moves the grid levels down but leaves the cost
 * basis alone, so the price leg of a round-trip across the ex-date books a loss of exactly
 * the dividend and this is the offsetting cash. It belongs to the symbol and the ex-date,
 * not to any one grid level.
 */
interface CreditRow {
  id: string;
  symbol: string;
  kind: string;
  amount: number;
  heldQty: number | null;
  cashPerShare: number | null;
  exDate: string;
  groupId: string;
  /** Withheld fraction `amount` was computed with. Needed for the row to add up. */
  taxRate?: number | null;
  /** 'derived' = recomputed here because the engine's own record never reached the archive. */
  source?: 'archived' | 'derived';
}
interface DailyResponse {
  points: DailyPoint[];
  cumulativeNetPnl: number;
}
interface ClosedPosition {
  positionId: string;
  symbol: string;
  type: string;
  /** Grid level the round-trip was taken on, raw VND/share like avgEntryPrice. */
  gridPoint: number | null;
  openedAt: string;
  closedAt: string | null;
  closedQty: number;
  avgEntryPrice: number | null;
  avgExitPrice: number | null;
  exitPriceSource: 'fills' | 'derived' | null;
  capital: number;
  realizedPnl: number;
  fee: number;
  /** This level's share of cash dividends, raw VND. Display-only: already in creditPnl. */
  dividendPnl?: number;
  /** Entry net of that dividend, raw VND/share. Null when the level crossed no ex-date. */
  adjustedEntryPrice?: number | null;
}
interface ClosedPositionsResponse {
  items: ClosedPosition[];
  /** Not paginated — complete regardless of `page`. */
  credits?: CreditRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface Props {
  portfolioId?: string | null;
  /** Optional: focus a single symbol. When falsy, shows the whole portfolio. */
  symbol?: string | null;
  refreshKey?: number;
  /** Only fetch/poll while the tab is visible. */
  active?: boolean;
}

const PAGE_SIZE = 20;
/** Server cap for closed-positions; the export pages through at this size. */
const EXPORT_PAGE_SIZE = 200;
const POLL_MS = 30_000;
const GREEN = '#0ECB81';
const RED = '#F6465D';
const MUTED = '#848E9C';

const stripExchange = (s: string): string => (s.includes(':') ? s.split(':').slice(1).join(':') : s);

const fmtVND = (n: number): string => formatInt(n, { fallback: '0' });

const signed = (n: number): string => `${n > 0 ? '+' : ''}${fmtVND(n)}`;

const pnlColor = (n: number): string => (n > 0 ? GREEN : n < 0 ? RED : MUTED);

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // numberFormat-exempt: date
  return d.toLocaleString('vi-VN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const CSV_HEADER = [
  'Closed at', 'Opened at', 'Symbol', 'Side', 'Grid point', 'Entry', 'Entry (dividend-adjusted)',
  'Exit', 'Exit source', 'Qty', 'Fee', 'Trading PnL', 'Dividend PnL', 'Realized PnL',
];

/** One row per round-trip, raw VND. Realized PnL = trading + the level's dividend share, as in the table. */
export const closedPositionsCsv = (items: ClosedPosition[]): string =>
  toCsv(
    CSV_HEADER,
    items.map((r) => {
      const div = r.dividendPnl ?? 0;
      return [
        r.closedAt, r.openedAt, r.symbol, r.type, r.gridPoint, r.avgEntryPrice, r.adjustedEntryPrice ?? null,
        r.avgExitPrice, r.exitPriceSource, r.closedQty, r.fee, r.realizedPnl, div, r.realizedPnl + div,
      ];
    }),
  );

const GridPerformanceTab: React.FC<Props> = ({ portfolioId, symbol, refreshKey, active = true }) => {
  const { message } = App.useApp();
  const [summary, setSummary] = useState<ProfitSummary | null>(null);
  const [daily, setDaily] = useState<DailyResponse | null>(null);
  const [closed, setClosed] = useState<ClosedPositionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // null = lifetime (no date filter). [from, to] inclusive, app-local days.
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  // null = every symbol; the prop, when given, is only the starting value
  const [symbolFilter, setSymbolFilter] = useState<string | null>(symbol ? stripExchange(symbol) : null);
  const [exporting, setExporting] = useState(false);

  const bareSymbol = symbolFilter;
  const fromStr = range ? range[0].format('YYYY-MM-DD') : null;
  const toStr = range ? range[1].format('YYYY-MM-DD') : null;

  const fetchAll = useCallback(
    async (silent: boolean) => {
      if (!portfolioId) return;
      const base = `/grid/portfolio/${portfolioId}/profit`;
      const symParam = bareSymbol ? { symbol: bareSymbol } : {};
      const dateParam = fromStr && toStr ? { from: fromStr, to: toStr } : {};
      try {
        if (!silent) setLoading(true);
        const [s, d, c] = await Promise.all([
          coreService.get<ProfitSummary>(`${base}/summary`, { params: dateParam }),
          coreService.get<DailyResponse>(`${base}/daily`, { params: { ...symParam, ...dateParam } }),
          coreService.get<ClosedPositionsResponse>(`${base}/closed-positions`, {
            params: { ...symParam, ...dateParam, page, pageSize: PAGE_SIZE },
          }),
        ]);
        setSummary(s.data);
        setDaily(d.data);
        setClosed(c.data);
      } catch (e: unknown) {
        if (!silent) {
          const msg = e instanceof Error ? e.message : String(e);
          message.error(`Failed to load grid performance: ${msg}`);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [portfolioId, bareSymbol, fromStr, toStr, page, message],
  );

  useEffect(() => {
    if (!active || !portfolioId) return;
    let cancelled = false;
    void (async () => {
      if (cancelled) return;
      await fetchAll(false);
    })();
    const id = window.setInterval(() => { if (!cancelled) void fetchAll(true); }, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [active, portfolioId, refreshKey, fetchAll]);

  const matchesFilter = useCallback(
    (sym: string) => !bareSymbol || stripExchange(sym) === bareSymbol,
    [bareSymbol],
  );
  const credits = useMemo(() => (closed?.credits ?? []).filter((c) => matchesFilter(c.symbol)), [closed, matchesFilter]);
  const uncounted = useMemo(
    () => (summary?.uncountedCredits ?? []).filter((u) => matchesFilter(u.symbol)),
    [summary, matchesFilter],
  );

  // The summary endpoint is portfolio-wide; a filtered view reads that symbol's line of it.
  const totals = useMemo(() => {
    if (!summary) return null;
    if (!bareSymbol) {
      return { netPnl: summary.netPnl, grossPnl: summary.grossPnl, creditPnl: summary.creditPnl, totalFee: summary.totalFee };
    }
    const row = summary.bySymbol.find((b) => stripExchange(b.symbol) === bareSymbol);
    return {
      netPnl: row?.netPnl ?? 0,
      grossPnl: row?.grossPnl ?? 0,
      creditPnl: row?.creditPnl ?? 0,
      totalFee: row?.fee ?? 0,
    };
  }, [summary, bareSymbol]);

  const symbolOptions = useMemo(() => {
    const syms = new Set((summary?.bySymbol ?? []).map((b) => stripExchange(b.symbol)));
    // keep the chosen one listed even when the date range leaves it without a trade
    if (bareSymbol) syms.add(bareSymbol);
    return Array.from(syms).sort().map((s) => ({ value: s, label: s }));
  }, [summary, bareSymbol]);

  const exportCsv = async () => {
    if (!portfolioId) return;
    setExporting(true);
    try {
      const params = {
        ...(bareSymbol ? { symbol: bareSymbol } : {}),
        ...(fromStr && toStr ? { from: fromStr, to: toStr } : {}),
        pageSize: EXPORT_PAGE_SIZE,
      };
      const items: ClosedPosition[] = [];
      for (let p = 1; ; p += 1) {
        const res = await coreService.get<ClosedPositionsResponse>(
          `/grid/portfolio/${portfolioId}/profit/closed-positions`,
          { params: { ...params, page: p } },
        );
        items.push(...res.data.items);
        if (res.data.items.length === 0 || items.length >= res.data.total) break;
      }
      if (items.length === 0) {
        message.info('Nothing to export for this filter.');
        return;
      }
      const scope = [bareSymbol ?? 'all', fromStr && toStr ? `${fromStr}_${toStr}` : 'lifetime'].join('_');
      downloadCsv(`grid-performance_${scope}.csv`, closedPositionsCsv(items));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      message.error(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  // Cumulative net series for the chart.
  const chartData = useMemo(() => {
    if (!daily?.points?.length) return [] as { date: string; cum: number }[];
    let cum = 0;
    return daily.points.map((p) => {
      cum += p.netPnl;
      return { date: p.date.slice(5), cum }; // MM-DD
    });
  }, [daily]);

  const closedColumns: ColumnsType<ClosedPosition> = useMemo(
    () => [
      {
        title: 'Time', dataIndex: 'closedAt', key: 'closedAt', width: 150,
        render: (v: string | null) => <Text style={{ fontSize: 11 }}>{formatDateTime(v)}</Text>,
      },
      {
        title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 80,
        render: (s: string, r) => (
          <span style={{ fontSize: 11 }}>
            <b>{s}</b>{' '}
            <Tag color={r.type === 'LONG' ? 'green' : 'red'} style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
              {r.type}
            </Tag>
          </span>
        ),
      },
      {
        // WHICH level took this profit. Not the same as Entry: the grid books a fill at
        // the level its order was PLACED at, so two levels that both filled at 32.0 are
        // two rows with one entry price and two different grid points.
        title: 'Grid point', dataIndex: 'gridPoint', key: 'gridPoint', align: 'right', width: 88,
        render: (v: number | null) => (
          <Text style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color: v == null ? MUTED : '#E0E0E0' }}>
            {v == null ? '—' : fmtVND(v)}
          </Text>
        ),
      },
      {
        // Dividend-adjusted when the level held through a cash ex-date, so Entry, Exit and
        // R.PnL agree; the real cost basis stays in the tooltip.
        title: 'Entry', dataIndex: 'avgEntryPrice', key: 'avgEntryPrice', align: 'right', width: 90,
        render: (v: number | null, r) => {
          const adj = r.adjustedEntryPrice;
          if (v == null) return <Text style={{ fontSize: 11, color: MUTED }}>—</Text>;
          if (adj == null) return <Text style={{ fontSize: 11, color: MUTED }}>{fmtVND(v)}</Text>;
          return (
            <Text
              style={{ fontSize: 11, color: MUTED }}
              title={`${fmtVND(v)} before the cash dividend (−${fmtVND(v - adj)}/share)`}
            >
              {fmtVND(adj)}
              <span style={{ color: GREEN, marginLeft: 2 }}>ᴰ</span>
            </Text>
          );
        },
      },
      {
        title: 'Exit', dataIndex: 'avgExitPrice', key: 'avgExitPrice', align: 'right', width: 96,
        render: (v: number | null, r) => (
          <Text style={{ fontSize: 11, color: '#E0E0E0' }}>
            {v != null ? fmtVND(v) : '—'}
            {v != null && r.exitPriceSource === 'derived' && (
              <span title="Derived from realizedPnl (fill data missing)" style={{ color: MUTED, marginLeft: 2 }}>*</span>
            )}
          </Text>
        ),
      },
      {
        title: 'Qty', dataIndex: 'closedQty', key: 'closedQty', align: 'right', width: 70,
        render: (v: number) => <Text style={{ fontSize: 11 }}>{fmtVND(v)}</Text>,
      },
      {
        title: 'Fee', dataIndex: 'fee', key: 'fee', align: 'right', width: 90,
        render: (v: number) => <Text style={{ fontSize: 11, color: MUTED }}>{fmtVND(v)}</Text>,
      },
      {
        title: 'R.PnL', dataIndex: 'realizedPnl', key: 'realizedPnl', align: 'right', width: 100,
        render: (v: number, r) => {
          const div = r.dividendPnl ?? 0;
          const total = v + div;
          return (
            <Text
              style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: pnlColor(total) }}
              title={div > 0 ? `Trading ${signed(v)} + cash dividend ${signed(div)}` : undefined}
            >
              {signed(total)}
            </Text>
          );
        },
      },
    ],
    [],
  );

  if (!portfolioId) {
    return <div style={emptyStyle}>Select a portfolio to view grid performance.</div>;
  }

  return (
    <div className="grid-performance-tab" style={wrapStyle}>
      <style>{`
        .grid-performance-tab .ant-table,
        .grid-performance-tab .ant-table-thead > tr > th,
        .grid-performance-tab .ant-table-tbody > tr > td,
        .grid-performance-tab .ant-table-cell,
        .grid-performance-tab .ant-table-placeholder,
        .grid-performance-tab .ant-table-placeholder > td { background: transparent !important; }
        .grid-performance-tab .ant-table-tbody > tr.ant-table-row:hover > td { background: rgba(255,255,255,0.03) !important; }
        .grid-performance-tab .ant-table-thead > tr > th { color: #848E9C; font-weight: 600; }
      `}</style>

      <Spin spinning={loading} size="small">
        <div style={toolbarStyle}>
          <Text style={{ color: MUTED, fontSize: 11 }}>Date range</Text>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => {
              setPage(1);
              setRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
            }}
            format="YYYY-MM-DD"
            allowClear
            size="small"
            placeholder={['All', 'All']}
            disabledDate={(cur) => cur && cur > dayjs().endOf('day')}
            presets={[
              { label: 'Today', value: [dayjs(), dayjs()] },
              { label: '7 days', value: [dayjs().subtract(6, 'day'), dayjs()] },
              { label: '30 days', value: [dayjs().subtract(29, 'day'), dayjs()] },
              { label: 'This month', value: [dayjs().startOf('month'), dayjs()] },
            ]}
          />
          <Text style={{ color: MUTED, fontSize: 11 }}>Symbol</Text>
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All"
            aria-label="Symbol filter"
            value={bareSymbol ?? undefined}
            options={symbolOptions}
            onChange={(v?: string) => {
              setPage(1);
              setSymbolFilter(v ?? null);
            }}
            style={{ minWidth: 110 }}
          />
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={!closed?.total}
            onClick={() => void exportCsv()}
            style={{ marginLeft: 'auto' }}
          >
            Export CSV
          </Button>
        </div>

        {summary?.dataGapBefore && (
          <div style={gapBannerStyle}>
            ⚠ History before {summary.dataGapBefore.slice(0, 10)} may be incomplete (older bot builds only kept Redis).
          </div>
        )}

        {uncounted.length > 0 && (
          <div style={gapBannerStyle}>
            {uncounted.map((u) => (
              <div key={u.groupId}>
                ⚠ {u.symbol}: cash dividend {fmtVND(u.cashPerShare)} VND/share
                {u.exDate ? ` (ex-date ${u.exDate})` : ''} is missing from these numbers — the
                shares held on the ex-date were never recorded, so the amount cannot be
                computed.
              </div>
            ))}
          </div>
        )}

        <div style={summaryStyle}>
          <SummaryItem label="Net realized" value={totals?.netPnl} color={pnlColor(totals?.netPnl ?? 0)} strong />
          <SummaryItem label="Trading" value={totals?.grossPnl} color={pnlColor(totals?.grossPnl ?? 0)} />
          <SummaryItem label="Dividend" value={totals?.creditPnl} color={pnlColor(totals?.creditPnl ?? 0)} />
          <SummaryItem label="Total fee" value={totals?.totalFee} color={MUTED} />
        </div>

        {credits.length > 0 && (
          <div style={creditBoxStyle}>
            {credits.map((c) => (
              <div key={c.id} style={creditRowStyle}>
                <Text style={{ fontSize: 11, color: MUTED }}>
                  {c.kind === 'CORRECTION' ? 'Correction' : 'Cash dividend'} · <b style={{ color: '#E0E0E0' }}>{c.symbol}</b>
                  {' · ex-date '}{c.exDate}
                  {c.heldQty != null && c.cashPerShare != null
                    ? ` · ${fmtVND(c.heldQty)} sh × ${fmtVND(c.cashPerShare)}`
                    : ''}
                  {/* `cashPerShare` is GROSS and the amount is NET: without the rate the row
                      cannot be checked by eye. */}
                  {c.taxRate != null && c.taxRate > 0
                    ? ` − ${formatDecimal(c.taxRate * 100, 1)}% tax`
                    : ''}
                  {c.source === 'derived' && (
                    <Tag
                      color="default"
                      style={{ marginInlineStart: 6, marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                      title="Recomputed here: the bot booked the cash but its record never reached the archive"
                    >
                      derived
                    </Tag>
                  )}
                </Text>
                <Text
                  title={
                    'Rows marked ᴰ held through the ex-date: their Entry and R.PnL include their share '
                    + 'of this cash (hover them for the unadjusted figures). Shares still held keep theirs here until sold. '
                    + 'Net of the PIT withheld at source — an assumed rate, so reconcile against the cash the broker actually credited.'
                    + (c.source === 'derived'
                      ? ' This line was recomputed from the holding recorded on the ex-date.'
                      : '')
                  }
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: pnlColor(c.amount),
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    cursor: 'help',
                  }}
                >
                  {signed(c.amount)}
                </Text>
              </div>
            ))}
            {/* The caveats used to sit here as four lines of small print under a one-line
                row, which on a phone made the note longer than the thing it annotated. They
                are not dropped — the amount carries them, because the one that matters is
                that this figure is an assumed net and has to be reconciled. */}
          </div>
        )}

        {chartData.length > 0 && (
          <div style={{ height: 120, marginTop: 4 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gridCumFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2B3139" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: MUTED, fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#2B3139' }} minTickGap={20} />
                <YAxis
                  tick={{ fill: MUTED, fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  width={52}
                  tickFormatter={(v: number) => (Math.abs(v) >= 1e6 ? `${formatDecimal(v / 1e6, 1)}M` : `${Math.round(v / 1e3)}k`)}
                />
                <RTooltip
                  contentStyle={{ background: '#1E2329', border: '1px solid #2B3139', borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: MUTED }}
                  formatter={(v: number) => [fmtVND(v), 'Cumulative net']}
                />
                <Area type="monotone" dataKey="cum" stroke={GREEN} strokeWidth={1.5} fill="url(#gridCumFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <Table<ClosedPosition>
          rowKey="positionId"
          columns={closedColumns}
          dataSource={closed?.items ?? []}
          size="small"
          // Contained horizontal scroll: without it the column widths push the whole page
          // wider than the screen and the right-hand columns cannot be reached at all.
          scroll={{ x: 'max-content' }}
          style={{ marginTop: 8 }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: closed?.total ?? 0,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            size: 'small',
          }}
          locale={{ emptyText: 'No closed positions yet' }}
        />
      </Spin>
    </div>
  );
};

const wrapStyle: React.CSSProperties = {
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  height: '100%',
};

const emptyStyle: React.CSSProperties = {
  padding: 24, textAlign: 'center', color: MUTED, fontSize: 12,
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 4,
};

const summaryStyle: React.CSSProperties = {
  display: 'grid',
  // Four hard tracks are ~80px each on a phone, too narrow for "Net realized" or
  // "Pending withdraw", so the labels wrapped and the row ran past the screen. auto-fit
  // falls to two per row there and keeps four wherever there is room.
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 45%), 1fr))',
  gap: 8,
};

const creditBoxStyle: React.CSSProperties = {
  background: '#1A1D23',
  border: '1px solid #2B3139',
  borderRadius: 4,
  padding: '8px 10px',
  // Sat flush against the summary row above it.
  marginTop: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const creditRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 8,
};

const gapBannerStyle: React.CSSProperties = {
  background: 'rgba(240,185,11,0.12)',
  border: '1px solid rgba(240,185,11,0.4)',
  color: '#F0B90B',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 11,
  marginBottom: 4,
};

const SummaryItem: React.FC<{ label: string; value?: number; color: string; strong?: boolean }> = ({
  label, value, color, strong,
}) => (
  <div style={{ padding: '6px 10px', background: '#1A1D23', borderRadius: 4 }}>
    <div style={{ color: MUTED, fontSize: 11 }}>{label}</div>
    <div style={{ color, fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 600, fontSize: strong ? 15 : 13 }}>
      {value !== undefined ? signed(value) : '—'}
    </div>
  </div>
);

export default GridPerformanceTab;
